import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { appointmentService } from '../../api/services';

export const fetchAppointments = createAsyncThunk(
  'appointments/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await appointmentService.getCustomerAppointments(params);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch appointments.');
    }
  }
);

export const fetchAppointmentById = createAsyncThunk(
  'appointments/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await appointmentService.getById(id);
      return data;
    } catch (error) {
      return rejectWithValue('Failed to fetch appointment details.');
    }
  }
);

export const bookAppointment = createAsyncThunk(
  'appointments/book',
  async (appointmentData, { rejectWithValue }) => {
    try {
      const { data } = await appointmentService.book(appointmentData);
      return data;
    } catch (error) {
      const data = error.response?.data;
      let errorMsg = 'Failed to book appointment.';
      if (typeof data === 'string') {
        errorMsg = data;
      } else if (data?.detail) {
        errorMsg = data.detail;
      } else if (data?.non_field_errors) {
        errorMsg = Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;
      } else if (typeof data === 'object' && data !== null) {
        const firstKey = Object.keys(data)[0];
        const val = data[firstKey];
        errorMsg = Array.isArray(val) ? val[0] : String(val);
      }
      return rejectWithValue(errorMsg);
    }
  }
);

export const cancelAppointment = createAsyncThunk(
  'appointments/cancel',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await appointmentService.cancel(id);
      return { id, ...data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail || 'Failed to cancel appointment.'
      );
    }
  }
);

const initialState = {
  appointments: [],
  selectedAppointment: null,
  loading: false,
  bookingLoading: false,
  error: null,
  bookingSuccess: false,
  bookingResult: null,
};

const appointmentSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    clearBookingState: (state) => {
      state.bookingSuccess = false;
      state.bookingResult = null;
      state.error = null;
    },
    clearSelectedAppointment: (state) => {
      state.selectedAppointment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action) => {
        state.loading = false;
        state.appointments = action.payload.results || action.payload;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAppointmentById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAppointmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAppointment = action.payload;
      })
      .addCase(fetchAppointmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(bookAppointment.pending, (state) => {
        state.bookingLoading = true;
        state.error = null;
        state.bookingSuccess = false;
      })
      .addCase(bookAppointment.fulfilled, (state, action) => {
        state.bookingLoading = false;
        state.bookingSuccess = true;
        state.bookingResult = action.payload;
        state.appointments.unshift(action.payload);
      })
      .addCase(bookAppointment.rejected, (state, action) => {
        state.bookingLoading = false;
        state.error = action.payload;
      })
      .addCase(cancelAppointment.fulfilled, (state, action) => {
        const idx = state.appointments.findIndex(
          (a) => a.id === action.payload.id
        );
        if (idx !== -1) {
          state.appointments[idx].status = 'cancelled';
        }
      });
  },
});

export const { clearBookingState, clearSelectedAppointment } =
  appointmentSlice.actions;
export default appointmentSlice.reducer;
